import * as Sentry from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '@/components/feedback/snackbar';
import { odooApiService } from '@/services/odoo.service';
import type { OdooResourceType, ManualLinkResult } from '@/types/odoo.types';

interface UseOdooLinkOptions {
  /** The Odoo entity type. */
  resourceType: OdooResourceType;
  /** Called after a successful link operation. */
  onSuccess?: (result: ManualLinkResult) => void;
}

/**
 * Hook that provides a mutation to link a Hub resource to an existing Odoo record.
 */
export function useOdooLink({ resourceType, onSuccess }: UseOdooLinkOptions) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const { mutate, mutateAsync, isLoading, error } = useMutation({
    mutationFn: ({ resourceId, odooId }: { resourceId: string; odooId: number }) =>
      odooApiService.link(resourceType, resourceId, odooId),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['odoo-search', resourceType] });
      queryClient.invalidateQueries({ queryKey: ['odoo-unlinked', resourceType] });
      enqueueSnackbar(t('Resource linked to Odoo successfully'), { variant: 'success' });
      onSuccess?.(result);
    },
    onError: (err: unknown) => {
      const error = err as { message?: string };
      const message = error?.message || t('An error occurred while linking to Odoo');
      enqueueSnackbar(message, { variant: 'error' });
      Sentry.captureException(err, {
        tags: { hook: 'useOdooLink', action: 'link', resourceType },
      });
    },
  });

  return {
    /** Trigger the link mutation (fire-and-forget). */
    link: mutate,
    /** Trigger the link mutation (returns a promise). */
    linkAsync: mutateAsync,
    /** True while the mutation is in progress. */
    isLoading,
    /** Error from the mutation, if any. */
    error,
  };
}
